<template>
  <div class="minds-dashboard-root">
    <v-wait for="isFetchingDashboard" class="filters">
      <div class="filter-selector-container">
        <v-select
          :value="selectedPlantId"
          :items="plants"
          :item-value="'_id'"
          :label="$t('views.minds.selector.factory')"
          class="custom plant-select"
          hide-details
          dense
          @change="onPlantChange"
        >
          <template
            slot="selection"
            slot-scope="data"
          >
            {{ data.item.identifier }}
          </template>
          <template
            slot="item"
            slot-scope="data"
          >
            {{ data.item.identifier }}
          </template>
        </v-select>
        <v-select
          v-model="asphaltMixId"
          :items="plantAsphaltMixes"
          :item-value="'_id'"
          :label="$t('views.minds.selector.mix')"
          hide-details
          class="dualData mix-select"
        >
          <template
            slot="selection"
            slot-scope="data"
          >
            <div class="data-container">
              <span>{{ data.item.identifier }}</span>
              <span v-if="data.item.tonnage">
                {{ 
                  $t('views.minds.selector.factory-tons', {
                    n: data.item.tonnage,
                  })
                }}
              </span>
            </div>
          </template>
          <template
            slot="item"
            slot-scope="data"
          >
            {{ data.item.identifier }}
          </template>
        </v-select>
      </div>
      <v-menu
        v-model="isDetailedSelectOpen"
        :close-on-content-click="false"
        :open-on-hover="true"
        offset-y
        :nudge-top="-4"
      >
        <template #activator="{ on, attrs }">
          <div 
            class="daily-production-trigger" 
            v-bind="attrs"
            v-on="on"
          >
            {{$t('views.minds.dailyProduction.triggerLabel')}}
          </div>
        </template>
        <detailed-select
          v-model="asphaltMixId"
          :columns="getColumnsSettingsDailyProduction"
          :items="plantAsphaltMixes"
          style="max-height: 70vh;"
        >
          <template #cell-identifier="{ item }">
            <!-- When cell.key = 'identifier', override the slot template with this -->
             <div class="d-flex flex-column indentifier-container">
              <span>{{ item.identifier }}</span>
              <span class="color-text-secondary">{{ item.mixDesignCode }}</span>
            </div>
          </template>
        </detailed-select>
      </v-menu>
    </v-wait>
    <v-wait for="isFetchingData" class="content">
      <core-spinner slot="waiting" />
      <img src="/brand/daway-logo-icon-min.svg" class="background-logo" alt="Daway Logo" />
      <div class="tables">
        <div class="table-dosage">
          <table-content
            :title="$t('views.minds.dosageChart.title')"
            :columns="getColumnsSettingsDosages"
            :items="hotBinReadings"
            :summary-row="chartSummaryType.TOTAL"
            :unit="$t('views.minds.units.kilogram')"
            :unit-position="chartUnitPosition.TITLE"
            :full-height="!isMobile"
            dense
            disable-pagination
            fixed-header
            height="100%"
            hide-default-footer
          />
        </div>
        <div class="table-results">
          <table-content
            :title="$t('views.minds.production.title')"
            :columns="getColumnsSettingsResults"
            :items="getLabResults"
            dense
            disable-pagination
            fixed-header
            height="100%"
            hide-default-footer
          />
        </div>
      </div>
      <div class="chart-dosage">
        <line-chart 
          id="chart-dosage"
          :title="$t('views.minds.dosageChart.title')"
          :subtitle="$t('views.minds.dosageChart.subtitle')"
          :legend-primary="$t('views.minds.dosageChart.legend.primary')"
          :legend-secondary="$t('views.minds.dosageChart.legend.secondary')"
          :data="hotBinReadingsLineChart"
          :category-labels="$t('views.minds.10batchesLabels')"
          :unit="$t('views.minds.units.kilogram')"
          :unit-position="chartUnitPosition.TITLE"
        />
      </div>
      <div class="chart-temperature">
        <bar-chart 
          id="chart-temperature"
          :title="$t('views.minds.temperature.title')"
          :subtitle="$t('views.minds.temperature.subtitle')"
          :legend-primary="$t('views.minds.temperature.legend.primary')"
          :legend-secondary="$t('views.minds.temperature.legend.secondary')"
          :data="temperatureReadings"
          :category-labels="$t('views.minds.10batchesLabels')"
          :status-messages="$t('views.minds.statusMessageTemperature')"
          :unit="$t('views.minds.units.celsius')"
          :unit-position="chartUnitPosition.AXIS_LABEL"
        />
      </div>
      <div class="chart-coldbins">
        <bar-chart 
          id="chart-coldbins"
          :title="$t('views.minds.coldBin.title')"
          :subtitle="$t('views.minds.coldBin.subtitle')"
          :legend-primary="$t('views.minds.coldBin.legend.primary')"
          :legend-secondary="$t('views.minds.coldBin.legend.secondary')"
          :data="coldBinReadings"
          :unit="$t('views.minds.units.percent')"
          :unit-position="chartUnitPosition.AXIS_LABEL"
          :value-scale="20"
          :direction="chartDirection.ROW"
        />
      </div>
    </v-wait>
  </div>
</template>

<script>
import { mapGetters, mapState } from 'vuex'
import { chartDirection, chartSummaryType, chartUnitPosition } from '@/utils/enum'
import BarChart from '../Charts/Bar/BarChart.vue'
import DetailedSelect from '@/components/core/DetailedSelect.vue'
import LineChart from '../Charts/Lines/LineChart.vue'
import TableContent from '../../DashViews/Charts/Tables/TableContent.vue'

export default {
  components: {
    BarChart,
    DetailedSelect,
    LineChart,
    TableContent,
  },
  props: {
    plant: {
      type: String,
      default: '',
    },
    asphaltMix: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      chartDirection,
      chartSummaryType,
      chartUnitPosition,
      isDetailedSelectOpen: false,
      selectedMixIdentifier: '',
    }
  },
  computed: {
    ...mapState('minds', [
      'coldBinReadings',
      'hotBinReadings',
      'labResults',
      'plants',
      'temperatureReadings',
      'selectedPlantId',
      'selectedAsphaltMixId',
    ]),
    ...mapGetters('minds', [
      'plantAsphaltMixes',
    ]),
    asphaltMixId: {
      get() {
        return this.selectedAsphaltMixId
      },
      set(value) {
        this.$store.commit(
          'minds/selectAsphaltMix', value
        )
        this.$router.replace({
          ...this.$router.currentRoute,
          query: {
            plant: this.selectedPlantId,
            asphaltMix: value,
          },
        })
      },
    },
    hotBinReadingsLineChart() {
      return this.hotBinReadings?.map(x => ({
        name: x.settings,
        base: x.target,
        values: x.values,
      }))
    },
    getColumnsSettingsDailyProduction() {
      const columns = [{
        header: { 
          text: this.$t('views.minds.dailyProduction.mix'), sortable: false
        },
        cell: { key: 'identifier' }
      },
      {
        header: { 
          text: this.$t('views.minds.dailyProduction.tons'), sortable: false, align: 'end'
        },
        cell: { key: 'dailyProduction' }
      }];
      return columns;
    },
    getColumnsSettingsDosages() {
      return [{
        header: { 
          text: this.$t('views.minds.dosageGrid.columnsLabel')[0], sortable: false,
          width: {
            xs: '92px',
            xl: '130px',
          },
        },
        cell: { key: 'settings' }
      },
      {
        header: { text: this.$t('views.minds.dosageGrid.columnsLabel')[1], sortable: false, align: 'end' }, 
        cell: { key: 'theoretical', displayInSummaryRow: true, decimals: 1 }
      },
      {
        header: { text: this.$t('views.minds.dosageGrid.columnsLabel')[2], sortable: false, align: 'end' },
        cell: { key: 'target', displayInSummaryRow: true, decimals: 1 }
      },
      {
        header: { text: this.$t('views.minds.dosageGrid.columnsLabel')[3], sortable: false, align: 'end' },
        cell: { key: 'current', displayInSummaryRow: true, decimals: 1 }
      }];
    },
    getColumnsSettingsResults() {
      return [{
        header: { 
          text: this.$t('views.minds.production.columnsLabel')[0], sortable: true,
          width: {
            xs: '92px',
            lg: '140px',
          },
        },
        cell: { key: 'settings' }
      },
      {
        header: { text: this.$t('views.minds.production.columnsLabel')[1], sortable: false, align: 'end' },
        cell: { key: 'estimated' }
      },
      {
        header: { text: this.$t('views.minds.production.columnsLabel')[2], sortable: false, align: 'end' },
        cell: { key: 'last' }
      },
      {
        header: { text: this.$t('views.minds.production.columnsLabel')[3], sortable: false, align: 'end' },
        cell: { key: 'mix' }
      }];
    },
    getLabResults() {
      return this.labResults ? this.labResults.map(x => {
        return {
          settings: this.$t('enums.productionSettings.' + x.setting),
          estimated: x.extimated,
          last: x.last,
          mix: x.mix,
          status: x.status
        }
      }) : []
    },
    isMobile() {
      return this.$vuetify.breakpoint.mdAndDown;
    }
  },
  watch: {
    async plant() {
      this.$store.commit(
        'minds/selectPlant',
        this.plant || this.plants[0]?._id,
      )
      this.$store.commit(
        'minds/selectAsphaltMix',
        this.asphaltMix || this.plantAsphaltMixes[0]?._id,
      )
      if (this.plantAsphaltMixes.length > 0) {
        const selectedMix = this.plantAsphaltMixes.find(mix => mix._id === this.selectedAsphaltMixId);
        if (selectedMix) {
          this.selectedMixIdentifier = selectedMix.identifier;
        }
      }
    },
    async asphaltMix() {
      this.$store.commit(
        'minds/selectAsphaltMix', this.asphaltMix
      )
      this.$store.dispatch('minds/fetchMindsData', {
        asphaltMixId: this.asphaltMix
      })
    },
  },
  async created() {
    this.$wait.start('isFetchingDashboard')
    this.$wait.start('isFetchingData')

    await this.$store.dispatch('minds/fetchPlants')

    if (this.plants.length > 0) {
      this.$store.commit(
        'minds/selectPlant',
        this.plant || this.selectedPlantId || this.plants[0]._id,
      )
    }

    if (this.plantAsphaltMixes.length > 0) {
      this.$store.commit(
        'minds/selectAsphaltMix',
        this.asphaltMix || this.selectedAsphaltMixId || this.plantAsphaltMixes[0]._id,
      )
      
      if (this.selectedAsphaltMixId != null) {
        await this.$store.dispatch('minds/fetchMindsData', {
          asphaltMixId: this.selectedAsphaltMixId
        })
      }
    }

    this.$wait.end('isFetchingDashboard')
    this.$wait.end('isFetchingData')
  },
  methods: { 
    onPlantChange(item) {
      this.$router.replace({
        ...this.$router.currentRoute,
        query: { plant: item },
      })
    },
    async onAsphaltMixChange() {
      if (this.selectedAsphaltMixId != null) {
        await this.$store.dispatch('minds/fetchMindsData', {
          asphaltMixId: this.selectedAsphaltMixId
        });
      }
    },
    onAsphaltMixSelect(mix) {
      this.selectedMixIdentifier = mix.identifier;
      this.isDetailedSelectOpen = false;
      this.onAsphaltMixChange();
    }
  },
}
</script>

<style scoped lang="scss">
@import '~vuetify/src/styles/styles.sass';
@import '@/styles/material-dashboard/variables.scss';

$grid-spacing: $spacing-md;

.minds-dashboard-root {
  position: relative;
  overflow-x: hidden;
}

.filters {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 $grid-spacing;

  .filter-selector-container {
    display: flex;
    flex-direction: row;
    gap: 12px;
  }
  .plant-select{
    max-width: 360px;
  }
  .mix-select {
    max-width: 360px;
  }
  .daily-production-trigger {
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
    background: $background-paper;
    border: thin solid $divider-light;
    border-radius: 1rem;
    font-family: $heading-font-family;
    font-weight: 600;
    padding: 10px 32px;
    text-wrap: nowrap;
    align-self: stretch;

    &::after {
      content: '';
      position: absolute;
      top: 100%;
      left: 0;
      right: 0;
      height: 8px; // nudge-top doubled
      background: transparent;
    }
    
  }
}
::v-deep(.indentifier-container) {
  gap: 4px;
}

.content {
  display: grid;
  grid-template-columns: minmax(0, 340px) minmax(0, 1fr) minmax(0, 1fr);
  grid-template-rows: repeat(2, minmax(0, 1fr));
  gap: $grid-spacing;
  padding: $grid-spacing;
  height: 100%;
  min-height: 0;

  .background-logo {
    display: none;
    position: absolute;
    width: 26vh;
    right: -22px;
    bottom: 50%;
    transform: translateY(80px);
    filter: $blue-900;
    opacity: 0.03;
  }
  
}
.tables {
  grid-row: 1 / span 2;
  grid-column: 1 / span 1;
}
.chart-dosage {
  grid-row: 1 / span 1;
  grid-column: 2 / span 2;
  max-width: 1100px;
}
.chart-temperature  {
  grid-row: 2 / span 1;
  grid-column: 2 / span 1;
}
.chart-coldbins {
  grid-row: 2 / span 1;
  grid-column: 3 / span 1;
}

@media #{map-get($display-breakpoints, 'lg-and-down')} {
  .tables {
    display: flex;
    flex-direction: column;
    gap: $grid-spacing;
    height: 100%;
    min-height: 0;

    .table-dosage {
      flex: 0 0 auto; // Hug content
      max-height: 50%;

      height: auto;
      min-height: 44px;
    }

    .table-results {
      flex: 1 1 auto; // Stretch to fill
      min-height: 0; // Prevent overflow
    }
  }
}
@media #{map-get($display-breakpoints, 'lg-and-up')} {
  .content {
    grid-template-columns: minmax(0, 460px) minmax(0, 1fr) minmax(0, 1fr);
    .background-logo {
      display: block;
    }
  }
}
@media #{map-get($display-breakpoints, 'xl-only')} {
  .tables {
    display: grid;
    grid-template-columns: minmax(0, 1fr);
    grid-template-rows: repeat(2, minmax(0, 1fr));
    gap: $grid-spacing;

    .table-dosage {
      grid-row: 1 / span 1;
      grid-column: 1 / span 1;
    }
    .table-results {
      grid-row: 2 / span 1;
      grid-column: 1 / span 1;
    }
  }
}

</style>
